import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Bell,
    MessageSquare,
    FolderOpen,
    Calendar,
    Users,
    Settings,
    FlaskConical,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Menu as MenuIcon,
    LogOut,
    User,
    Square,
} from 'lucide-react';
import { StaffApis } from 'apis/staff';
import logoImage from 'assets/logo/logo_icon_no_bg.png';
import { cn } from 'lib/utils';

interface SubMenu {
    title: string;
    to: string;
}

interface Menu {
    title: string;
    subMenus?: SubMenu[];
    to?: string;
    /** 하위 메뉴 링크와 경로가 달라도 이 상위 메뉴를 활성으로 볼 경로들 (예: 현장 상세 /sites/123) */
    activePaths?: string[];
}

interface SideNavProps {
    menus: Menu[];
}

const MenuIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    메인: LayoutDashboard,
    공지사항: Bell,
    일반게시판: MessageSquare,
    현장자료실: FolderOpen,
    캘린더: Calendar,
    계정: Users,
    관리자: Settings,
    테스트: FlaskConical,
};

const renderIcon = (title: string, className: string) => {
    const Comp = MenuIconMap[title] || Square;
    return <Comp className={className} />;
};

const SideNav: React.FC<SideNavProps> = ({ menus }) => {
    const getInitialExpandedMenus = useCallback(() => {
        const initial = new Set<string>();
        menus.forEach((menu) => {
            if (menu.subMenus && menu.subMenus.length > 0) {
                initial.add(menu.title);
            }
        });
        return initial;
    }, [menus]);

    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(getInitialExpandedMenus);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
    const [contextMenuPosition, setContextMenuPosition] = useState<number>(0);
    const [userName, setUserName] = useState<string>('사용자');
    const [userRole, setUserRole] = useState<string>('일반');
    const [showUserMenu, setShowUserMenu] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const userMenuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const response = await StaffApis.getMyInfo();
                setUserName(response.name || '사용자');
                const typeMap: Record<string, string> = {
                    MASTER: '관리자',
                    STAFF: '직원',
                };
                setUserRole(typeMap[response.token?.type] || response.token?.type || '직원');
            } catch (error) {
                console.error('Failed to fetch user info:', error);
            }
        };
        fetchUserInfo();
    }, []);

    useEffect(() => {
        setExpandedMenus(getInitialExpandedMenus());
    }, [menus, getInitialExpandedMenus]);

    useEffect(() => {
        if (!isCollapsed && !showUserMenu) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-nav-item]') && !target.closest('[data-context-submenu]')) {
                setHoveredMenu(null);
            }
            if (!target.closest('[data-user-info]') && !target.closest('[data-user-dropdown]')) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isCollapsed, hoveredMenu, showUserMenu]);

    const toggleSubmenu = (menuTitle: string) => {
        const next = new Set(expandedMenus);
        if (next.has(menuTitle)) next.delete(menuTitle);
        else next.add(menuTitle);
        setExpandedMenus(next);
    };

    // 메뉴 링크는 쿼리를 가질 수 있다 (예: /sites/list?category=SUPERVISION).
    // 경로만 비교하면 같은 경로를 쓰는 형제 메뉴를 구분할 수 없으므로 쿼리까지 본다.
    const splitTo = (to: string): [string, string] => {
        const idx = to.indexOf('?');
        return idx === -1 ? [to, ''] : [to.slice(0, idx), to.slice(idx + 1)];
    };

    const pathMatches = useCallback(
        (path: string) =>
            location.pathname === path || location.pathname.startsWith(path + '/'),
        [location.pathname]
    );

    // 링크에 적힌 쿼리 파라미터가 모두 현재 URL과 일치하는지 (현재 URL의 추가 파라미터는 무시)
    const queryMatches = useCallback(
        (search: string) => {
            if (!search) return true;
            const current = new URLSearchParams(location.search);
            const target = new URLSearchParams(search);
            let matched = true;
            target.forEach((value, key) => {
                if (current.get(key) !== value) matched = false;
            });
            return matched;
        },
        [location.search]
    );

    const isMenuActive = useCallback(
        (menu: Menu) => {
            if (menu.activePaths?.some((path) => pathMatches(path))) return true;
            if (menu.to) {
                const [menuPath] = splitTo(menu.to);
                if (menuPath.startsWith('/board/')) return location.pathname === menuPath;
                return location.pathname.startsWith(menuPath);
            }
            if (menu.subMenus) {
                return menu.subMenus.some((sub) => {
                    const [subPath] = splitTo(sub.to);
                    if (subPath.startsWith('/board/')) return location.pathname === subPath;
                    return pathMatches(subPath);
                });
            }
            return false;
        },
        [location.pathname, pathMatches]
    );

    const isSubmenuActive = useCallback(
        (to: string, siblingPaths?: string[]) => {
            const [toPath, toSearch] = splitTo(to);
            if (toPath.startsWith('/board/')) return location.pathname === toPath;
            if (!pathMatches(toPath)) return false;
            if (!queryMatches(toSearch)) return false;

            // 경로/쿼리가 모두 맞는 형제들 중 가장 구체적인 하나만 활성 처리
            const candidates = (siblingPaths ?? [to]).filter((sibling) => {
                const [path, search] = splitTo(sibling);
                if (path.startsWith('/board/')) return false;
                return pathMatches(path) && queryMatches(search);
            });
            if (candidates.length === 0) return false;

            // 쿼리까지 일치하는 링크 우선, 그다음 경로가 더 깊은 링크 우선
            const score = (sibling: string) => {
                const [path, search] = splitTo(sibling);
                return (search ? 1000 : 0) + path.length;
            };
            const best = candidates.reduce((a, b) => (score(b) > score(a) ? b : a));
            return best === to;
        },
        [location.pathname, pathMatches, queryMatches]
    );

    const handleMenuClick = (menu: Menu, e?: React.MouseEvent) => {
        if (menu.subMenus && menu.subMenus.length > 0) {
            if (isCollapsed) {
                if (e) {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setContextMenuPosition(rect.top);
                }
                setHoveredMenu(hoveredMenu === menu.title ? null : menu.title);
            } else {
                toggleSubmenu(menu.title);
            }
        } else if (menu.to) {
            navigate(menu.to);
            setIsMobileOpen(false);
        }
    };

    const handleSubmenuClick = (to: string) => {
        navigate(to);
        setIsMobileOpen(false);
        setHoveredMenu(null);
    };

    const toggleCollapse = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        if (!next) setExpandedMenus(getInitialExpandedMenus());
    };

    const handleLogout = async () => {
        try {
            await StaffApis.logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
            navigate('/login');
        }
    };

    const handleMyPage = () => {
        navigate('/account/mypage');
        setShowUserMenu(false);
        setIsMobileOpen(false);
    };

    const userInitials = useMemo(() => {
        if (!userName) return 'U';
        return userName.slice(0, 1);
    }, [userName]);

    return (
        <>
            {/* Mobile Overlay */}
            <div
                className={cn(
                    'fixed inset-0 z-40 bg-neutral-900/40 transition-opacity duration-fast md:hidden',
                    isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                )}
                onClick={() => setIsMobileOpen(false)}
            />

            {/* Mobile Menu Button */}
            <button
                type="button"
                className="fixed top-3 left-3 z-50 inline-flex h-9 w-9 items-center justify-center rounded border border-border bg-background text-foreground md:hidden hover:bg-muted transition-colors"
                onClick={() => setIsMobileOpen((v) => !v)}
                aria-label="메뉴 열기"
            >
                <MenuIcon className="h-5 w-5" />
            </button>

            {/* Sidebar */}
            <aside
                className={cn(
                    'modern-sidenav fixed top-0 left-0 z-40 h-screen bg-background border-r border-border flex flex-col transition-[width,transform] duration-slow ease-standard',
                    isCollapsed ? 'collapsed w-20' : 'w-[280px]',
                    isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
                )}
            >
                <div className="flex flex-col h-full">
                    {/* Header — 흰 배경 + Navy 로고 액센트 (nav와 자연 연속) */}
                    <div className="border-b border-border bg-background">
                        <button
                            type="button"
                            className={cn(
                                'flex items-center w-full h-16 transition-colors duration-fast hover:bg-muted',
                                isCollapsed ? 'justify-center px-0' : 'px-4'
                            )}
                            onClick={() => navigate('/main/home')}
                        >
                            <div className="flex h-9 w-9 items-center justify-center rounded bg-primary shrink-0">
                                <img
                                    src={logoImage}
                                    alt="유원ENF"
                                    className="h-5 w-5 object-contain brightness-0 invert"
                                />
                            </div>
                            {!isCollapsed && (
                                <div className="ml-3 flex flex-col items-start text-left min-w-0">
                                    <span className="text-md font-semibold tracking-tight text-foreground truncate leading-tight">
                                        유원ENF
                                    </span>
                                    <span className="text-xs text-muted-foreground leading-tight mt-0.5">
                                        인트라넷 시스템
                                    </span>
                                </div>
                            )}
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 overflow-y-auto py-3">
                        {menus.map((menu, index) => {
                            const active = isMenuActive(menu);
                            const expanded = expandedMenus.has(menu.title);
                            const hasSub = menu.subMenus && menu.subMenus.length > 0;

                            return (
                                <div key={index} className="relative mb-0.5" data-nav-item>
                                    {/* 활성 상태 좌측 띠 — sidebar 좌측 edge에 붙임 (button 외곽) */}
                                    {active && !isCollapsed && (
                                        <span className="pointer-events-none absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                                    )}
                                    <button
                                        type="button"
                                        className={cn(
                                            'group w-full flex items-center gap-3 rounded mx-2 h-9 text-md font-medium transition-colors duration-fast',
                                            isCollapsed ? 'justify-center px-0 w-[calc(100%-1rem)]' : 'px-3 w-[calc(100%-1rem)]',
                                            // 하위 메뉴가 있는 그룹은 배경을 하위 항목에만 준다 (배경 이중 강조 방지)
                                            active && !hasSub
                                                ? 'bg-navy-50 text-primary font-semibold'
                                                : active
                                                  ? 'text-primary font-semibold hover:bg-muted'
                                                  : 'text-neutral-700 hover:bg-muted hover:text-foreground'
                                        )}
                                        onClick={(e) => handleMenuClick(menu, e)}
                                        title={isCollapsed ? menu.title : undefined}
                                    >
                                        <span
                                            className={cn(
                                                'inline-flex items-center justify-center shrink-0',
                                                active ? 'text-primary' : 'text-neutral-500'
                                            )}
                                        >
                                            {renderIcon(menu.title, 'h-[18px] w-[18px]')}
                                        </span>
                                        {!isCollapsed && (
                                            <>
                                                <span className="flex-1 text-left truncate">
                                                    {menu.title}
                                                </span>
                                                {hasSub && (
                                                    <ChevronDown
                                                        className={cn(
                                                            'h-3.5 w-3.5 text-neutral-400 transition-transform duration-fast',
                                                            expanded && 'rotate-180'
                                                        )}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </button>

                                    {/* Inline Submenu */}
                                    {!isCollapsed && hasSub && (
                                        <div
                                            className={cn(
                                                'overflow-hidden transition-[max-height] duration-slow ease-standard',
                                                expanded ? 'max-h-96' : 'max-h-0'
                                            )}
                                        >
                                            {/* mr-2: 상위 항목(mx-2)과 우측 여백을 맞춤 — 하이라이트가 사이드바 끝에 붙지 않게 */}
                                            <div className="mt-0.5 mb-1 ml-7 mr-2 pl-2 border-l border-border flex flex-col gap-0.5">
                                                {menu.subMenus!.map((sub, subIndex) => {
                                                    const siblingPaths = menu.subMenus!.map((s) => s.to);
                                                    const subActive = isSubmenuActive(sub.to, siblingPaths);
                                                    return (
                                                        <button
                                                            key={subIndex}
                                                            type="button"
                                                            className={cn(
                                                                'flex items-center h-8 px-3 rounded text-sm transition-colors duration-fast text-left',
                                                                subActive
                                                                    ? 'bg-navy-50 text-primary font-medium'
                                                                    : 'text-neutral-500 hover:bg-muted hover:text-foreground'
                                                            )}
                                                            onClick={() => handleSubmenuClick(sub.to)}
                                                        >
                                                            {sub.title}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Collapsed: floating context menu */}
                                    {isCollapsed && hasSub && hoveredMenu === menu.title && (
                                        <div
                                            data-context-submenu
                                            className="fixed left-[88px] z-50 min-w-[180px] rounded border border-border bg-popover py-1"
                                            style={{ top: contextMenuPosition }}
                                        >
                                            <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b border-border">
                                                {menu.title}
                                            </div>
                                            {menu.subMenus!.map((sub, subIndex) => {
                                                const siblingPaths = menu.subMenus!.map((s) => s.to);
                                                const subActive = isSubmenuActive(sub.to, siblingPaths);
                                                return (
                                                    <button
                                                        key={subIndex}
                                                        type="button"
                                                        className={cn(
                                                            'block w-full text-left px-3 py-1.5 text-base transition-colors',
                                                            subActive
                                                                ? 'bg-accent text-accent-foreground font-medium'
                                                                : 'text-neutral-700 hover:bg-muted'
                                                        )}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSubmenuClick(sub.to);
                                                        }}
                                                    >
                                                        {sub.title}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </nav>

                    {/* Footer — User */}
                    <div className="relative border-t border-border p-2" ref={userMenuRef}>
                        <button
                            type="button"
                            data-user-info
                            className={cn(
                                'w-full flex items-center gap-3 rounded px-2 py-2 hover:bg-muted transition-colors',
                                isCollapsed && 'justify-center'
                            )}
                            onClick={() => setShowUserMenu((v) => !v)}
                        >
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy-50 text-primary text-sm font-semibold shrink-0">
                                {userInitials}
                            </div>
                            {!isCollapsed && (
                                <div className="flex flex-col items-start min-w-0 flex-1">
                                    <span className="text-base font-medium text-foreground truncate w-full text-left">
                                        {userName}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        {userRole}
                                    </span>
                                </div>
                            )}
                        </button>

                        {showUserMenu && !isCollapsed && (
                            <div
                                data-user-dropdown
                                className="absolute bottom-full left-2 right-2 mb-1 rounded border border-border bg-popover py-1"
                            >
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-base text-foreground hover:bg-muted transition-colors"
                                    onClick={handleMyPage}
                                >
                                    <User className="h-4 w-4 text-neutral-500" />
                                    <span>마이페이지</span>
                                </button>
                                <div className="my-1 h-px bg-border" />
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-base text-destructive hover:bg-red-50 transition-colors"
                                    onClick={handleLogout}
                                >
                                    <LogOut className="h-4 w-4" />
                                    <span>로그아웃</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* Collapse Button */}
            <button
                type="button"
                onClick={toggleCollapse}
                className={cn(
                    'hidden md:flex fixed top-20 z-40 items-center justify-center h-7 w-7 rounded-full border border-border bg-background text-neutral-500 hover:text-foreground hover:bg-muted transition-[left,colors] duration-slow ease-standard',
                    isCollapsed ? 'left-[68px]' : 'left-[268px]'
                )}
                aria-label={isCollapsed ? '사이드바 펼치기' : '사이드바 접기'}
            >
                {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                ) : (
                    <ChevronLeft className="h-4 w-4" />
                )}
            </button>
        </>
    );
};

export default SideNav;
